import { z } from 'zod';

/**
 * The API response envelope (docs/spec.md §5).
 *
 * The server never sends Lao or English prose — it sends an `ErrorCode` and the
 * client translates it. `message` carries a developer-facing English string for
 * logs only; do not render it to clinic staff.
 */

export const errorCodes = [
  /** No usable session — the client should send the user to the login screen. */
  'UNAUTHENTICATED',
  /**
   * Wrong username or password. Separate from UNAUTHENTICATED so the login form
   * can say "check your details" instead of "please sign in again", which reads
   * as nonsense on the sign-in screen itself. It deliberately does not
   * distinguish an unknown user from a bad password.
   */
  'INVALID_CREDENTIALS',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'CONFLICT',
  'INSUFFICIENT_STOCK',
  'RATE_LIMITED',
  'INTERNAL',
] as const;
export const errorCodeSchema = z.enum(errorCodes);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const HTTP_STATUS_BY_ERROR_CODE = {
  UNAUTHENTICATED: 401,
  INVALID_CREDENTIALS: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION_FAILED: 422,
  CONFLICT: 409,
  INSUFFICIENT_STOCK: 409,
  RATE_LIMITED: 429,
  INTERNAL: 500,
} as const satisfies Record<ErrorCode, number>;

export interface ApiMeta {
  page: number;
  pageSize: number;
  total: number;
}

export interface ApiSuccess<T> {
  data: T;
  meta?: ApiMeta;
}

export interface ApiFailure {
  error: {
    code: ErrorCode;
    /** Developer-facing English text. Never shown to end users. */
    message: string;
    /** Field-level validation problems, keyed by form field path. */
    fields?: Record<string, string>;
  };
}

export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export function isApiFailure<T>(res: ApiResponse<T>): res is ApiFailure {
  return 'error' in res;
}

/* ---- Common query shapes ---- */

export const PAGE_SIZE_DEFAULT = 20;
export const PAGE_SIZE_MAX = 100;

/**
 * Every list endpoint accepts these. No endpoint may return an unbounded set
 * (docs/spec.md §5).
 */
export const listQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
  q: z.string().trim().max(120).optional(),
  sort: z.string().trim().max(60).optional(),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

/* ---- Health check (P0) ---- */

export const healthResponseSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  version: z.string(),
  uptimeSeconds: z.number(),
  time: z.string(),
  db: z.object({
    connected: z.boolean(),
    latencyMs: z.number().nullable(),
    error: z.string().nullable(),
  }),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;
